import { MoneyOffRounded as MoneyOffRoundedIcon } from 'components/icons';
import Chip from 'components/@extended/Chip.tsx';
import StatusChip from 'components/@extended/StatusChip';
import DmsLink from 'components/dmslink/DmsLink';
import MatterIndicators, { hasMatterAccess } from 'components/matters/MatterIndicators';
import { FormattedDate } from 'components/misc/FormattedDate';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import type { DataTableColumnDef } from 'components/tables/data-table';
import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { MatterListItem, MatterStateCountDto, MatterStateType } from 'types/matters';
import { getClassifierText, t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { cn } from 'utils/cn';
import { useClassifiers } from 'utils/useClassifiers';
import { MatterActionButtons } from '../buttons/MatterActionButtons';
import EditableMatterSequenceNumber from '../components/EditableMatterSequenceNumber';
import { ENTITY_LINK_CLASS, getStatusChipType } from '../util';
import MattersAccordionTable from './MattersAccordionTable';

type MatterGroup = MatterStateCountDto['matterGroups'][number]['groupName'];

type MatterStateGroup = 'inProgress' | 'pending' | 'completed' | 'offerWon' | 'offerFailed';

type Props = {
    group: MatterGroup;
    groupTitle: string;
    matterStateCount: MatterStateCountDto;
    contactIdList: number[];
    searchText: string;
    rowsPerPage?: number;
    disableUrlState?: boolean;
    showSequenceNumberColumn?: boolean;
};

const USED_CLASSIFIERS = ['MATTER_STATE_TYPE', 'BUSINESS_AREA_TYPE', 'EXPENSE_TYPE', 'CURRENCY'];
// About what a case row measures here (name, client and status chips stacked in the first column).
const ROW_HEIGHT_ESTIMATE = 120;

const getMatterHref = (matter: MatterListItem) => `/matter/${matter.id}`;

const MattersTableGrouped = (props: Props): JSX.Element => {
    const {
        group,
        groupTitle,
        matterStateCount,
        contactIdList,
        searchText,
        rowsPerPage,
        disableUrlState,
        showSequenceNumberColumn,
    } = props;

    const { classifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    const passiveFilter = useMemo(() => {
        switch (group) {
            case 'opponent':
                return `parties.party.id=in=(${contactIdList.join(',')});parties.matterPartyRoleTypeCode=in=('OPPONENT')`;
            case 'primaryContact':
                return `primaryContactParty.id=in=(${contactIdList.join(',')})`;
            case 'judge':
                return `parties.party.id=in=(${contactIdList.join(',')});parties.matterPartyRoleTypeCode=in=('JUDGE')`;
            case 'other':
                return `otherConnectedParty.id=in=(${contactIdList.join(',')})`;
            case 'connected':
                return `clientParty.id=in=(${contactIdList.join(',')})`;
            case 'payer':
                return `payers.party.id=in=(${contactIdList.join(',')})`;
        }
    }, [contactIdList, group]);

    const getTitle = useCallback(
        (id: string, type: MatterGroup) => {
            const stateCount = (matterStateCount.matterGroups || []).find(
                (item) => item.groupName === type
            )?.stateCount;
            const count = stateCount ? Object.values(stateCount).reduce((acc, curr) => (acc ?? 0) + (curr ?? 0), 0) : 0;
            return (
                <p className="pb-4 text-base/5.5 font-semibold">
                    <FormattedMessage id={id} values={{ count: count }} />
                </p>
            );
        },
        [matterStateCount]
    );

    const getCount = useCallback(
        (type: MatterGroup, state: MatterStateGroup) => {
            let matterStateType: string;
            switch (state) {
                case 'completed':
                    matterStateType = MatterStateType.COMPLETED;
                    break;
                case 'offerWon':
                    matterStateType = MatterStateType.OFFER_WON;
                    break;
                case 'offerFailed':
                    matterStateType = MatterStateType.OFFER_LOST;
                    break;
                case 'pending':
                    matterStateType = MatterStateType.OFFER_PENDING;
                    break;
                case 'inProgress':
                    matterStateType = MatterStateType.IN_PROGRESS;
                    break;
            }
            const stateCount = (matterStateCount.matterGroups || []).find(
                (item) => item.groupName === type
            )?.stateCount;
            return stateCount ? ((stateCount as unknown as Record<string, number>)[matterStateType] ?? 0) : 0;
        },
        [matterStateCount]
    );

    const getAccordionTitle = useCallback(
        (type: MatterGroup, state: MatterStateGroup): JSX.Element => {
            let title = '';
            switch (state) {
                case 'completed':
                    title = 'contacts.cases.completed';
                    break;
                case 'offerWon':
                    title = 'contacts.cases.offer-won';
                    break;
                case 'offerFailed':
                    title = 'contacts.cases.offer-failed';
                    break;
                case 'pending':
                    title = 'contacts.cases.pending';
                    break;
                case 'inProgress':
                    title = 'contacts.cases.in-progress';
                    break;
            }
            const count = getCount(type, state);
            return <FormattedMessage id={title} values={{ count }} />;
        },
        [getCount]
    );

    const getDefaultExpanded = useCallback(
        (type: MatterGroup, state: MatterStateGroup) => {
            if (getCount(type, state) === 0) {
                return false;
            }

            switch (state) {
                case 'inProgress':
                    return true;
                case 'pending':
                    return getCount(type, 'inProgress') === 0;
                case 'completed':
                    return getCount(type, 'inProgress') === 0 && getCount(type, 'pending') === 0;
                case 'offerWon':
                    return (
                        getCount(type, 'inProgress') === 0 &&
                        getCount(type, 'pending') === 0 &&
                        getCount(type, 'completed') === 0
                    );
                case 'offerFailed':
                    return (
                        getCount(type, 'inProgress') === 0 &&
                        getCount(type, 'pending') === 0 &&
                        getCount(type, 'completed') === 0 &&
                        getCount(type, 'offerWon') === 0
                    );
            }
        },
        [getCount]
    );

    const sequenceNumberColumn = useMemo<DataTableColumnDef<MatterListItem>>(
        () => ({
            header: t('matters.seq-nr'),
            id: 'clientMatterSequence',
            enableGrouping: false,
            enableSorting: false,
            meta: {
                widthWeight: 1,
                minPx: 110,
            },
            cell: ({ row }) => {
                const { id, clientMatterSequence, isClientMatterSequenceEditable } = row.original;
                if (!isClientMatterSequenceEditable) {
                    return null;
                }
                return <EditableMatterSequenceNumber matterId={id} clientMatterSequence={clientMatterSequence} />;
            },
        }),
        []
    );

    const columns = useMemo<DataTableColumnDef<MatterListItem>[]>(
        () => [
            {
                header: t('matters.name-in-column-header'),
                accessorKey: 'fullName',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    // Measured in the accordion: 675 px here against 245 px for each of the six data
                    // columns.
                    widthWeight: 2.76,
                    minPx: 240,
                },
                cell: ({ cell }) => {
                    const { id, fullName, clientParty, matterStateTypeCode, isNonBillable, isCurrentlyBlocked } =
                        cell.row.original;
                    return (
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                                <Link className={cn(ENTITY_LINK_CLASS, 'font-semibold')} to={`/matter/${id}`}>
                                    {fullName ?? '-'}
                                </Link>
                                {cell.row.original.dmsLink && (
                                    <DmsLink link={cell.row.original.dmsLink} isIconDisplayed={true} />
                                )}
                            </div>
                            {clientParty?.id ? (
                                <PartyName
                                    party={clientParty}
                                    isLink
                                    variant="list"
                                    showPartyNumber
                                    className="whitespace-nowrap"
                                />
                            ) : null}
                            <Link className="entity-link w-fit no-underline" to={`/matter/${id}`}>
                                <div className="flex gap-1">
                                    <MatterIndicators
                                        isConfidential={cell.row.original.isConfidential}
                                        hasAccess={hasMatterAccess(cell.row.original)}
                                        isBlocked={isCurrentlyBlocked}
                                        variant="chip"
                                        className="mt-[4px] cursor-pointer"
                                    />
                                    {isNonBillable ? (
                                        <Chip
                                            className="mt-1 cursor-pointer"
                                            size="small"
                                            label={<MoneyOffRoundedIcon />}
                                            type={'partyType'}
                                        />
                                    ) : null}
                                    <StatusChip
                                        className="mt-1 cursor-pointer"
                                        type={getStatusChipType(matterStateTypeCode)}
                                        label={getClassifierText(classifiers['MATTER_STATE_TYPE'], matterStateTypeCode)}
                                    />
                                </div>
                            </Link>
                        </div>
                    );
                },
            },
            ...(showSequenceNumberColumn ? [sequenceNumberColumn] : []),
            {
                header: t('matters.opening-date'),
                accessorKey: 'openedDate',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ getValue }) => {
                    const value = getValue<MatterListItem['openedDate']>();
                    return value ? <FormattedDate value={value} fallback="" /> : null;
                },
            },
            {
                header: t('assigned-to'),
                accessorKey: 'responsibleEmployee',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    // Floor kept at 140 (not the flat list's 180): the accordion is narrower, and 180
                    // pushes the summed floors past the container at 1366, adding a horizontal scrollbar.
                    minPx: 140,
                },
                cell: ({ cell }) => {
                    const { responsibleEmployee, supervisedByEmployee } = cell.row.original;
                    return (
                        <div>
                            {responsibleEmployee ? (
                                <div>
                                    <RecordLink
                                        className={ENTITY_LINK_CLASS}
                                        to={`/employee/${responsibleEmployee.id}`}
                                        label={responsibleEmployee.name}
                                    />
                                </div>
                            ) : null}
                            {supervisedByEmployee ? (
                                <div>
                                    <RecordLink
                                        className={ENTITY_LINK_CLASS}
                                        to={`/employee/${supervisedByEmployee.id}`}
                                        label={supervisedByEmployee.name}
                                    />
                                </div>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                header: t('matters.office'),
                accessorKey: 'handledByOffice',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 140,
                },
                cell: ({ getValue }) => {
                    const value = getValue<MatterListItem['handledByOffice']>();
                    if (!value) {
                        return null;
                    }
                    // `appreviation` is a typo of `abbreviation`, so the property is always undefined at runtime
                    // and the table shows `value.name`. Kept as a typed lookup rather than silently switching to
                    // the correct field, which would change what the column renders.
                    const name = (value as { appreviation?: string }).appreviation ?? value.name ?? '-';
                    return (
                        <RecordLink
                            className={ENTITY_LINK_CLASS}
                            to={`/office/${value.id}`}
                            label={`${name}${officeInactiveSuffix(value) ?? ''}`}
                        />
                    );
                },
            },
            {
                header: t('matters.uninvoiced-hours-total'),
                id: 'matterStats.totalTimeInHours',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ row }) => {
                    const { matterStats } = row.original;
                    const uninvoicedHours = matterStats?.uninvoicedBilledTimeInHours || 0;
                    const totalHours = matterStats?.totalBilledTimeInHours || 0;
                    return matterStats ? (
                        <div className="flex flex-col">
                            <p className="text-body1">
                                <HoursDisplay value={uninvoicedHours} />
                            </p>
                            <span className="text-caption text-fg-secondary">
                                <HoursDisplay value={totalHours} />
                            </span>
                        </div>
                    ) : null;
                },
            },
            {
                header: t('stats.uninvoiced-amount'),
                id: 'currencyDetails.uninvoicedAmountInOriginalCurrency',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 140,
                },
                cell: ({ row }) => {
                    const { currencyDetails } = row.original;
                    const amountOriginalCurr = currencyDetails?.uninvoicedAmountInOriginalCurrency ?? 0;
                    const amountBaseCurr = currencyDetails?.uninvoicedAmountInBaseCurrency ?? 0;
                    const currency = currencyDetails?.originalCurrency;
                    const baseCurrency = currencyDetails?.baseCurrency;

                    return currencyDetails ? (
                        <div className="flex flex-col">
                            <p className="text-body1">
                                <MoneyDisplay amount={amountOriginalCurr} currency={currency} />
                            </p>
                            <span className="text-caption text-fg-secondary">
                                <MoneyDisplay amount={amountBaseCurr} currency={baseCurrency} />
                            </span>
                        </div>
                    ) : null;
                },
            },
            {
                header: t('stats.total-amount'),
                id: 'currencyDetails.totalAmountInOriginalCurrency',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 140,
                },
                cell: ({ row }) => {
                    const { currencyDetails } = row.original;
                    const amountOriginalCurr = currencyDetails?.totalAmountInOriginalCurrency ?? 0;
                    const amountBaseCurr = currencyDetails?.totalAmountInBaseCurrency ?? 0;
                    const currency = currencyDetails?.originalCurrency ?? '';
                    const baseCurrency = currencyDetails?.baseCurrency ?? '';

                    return currencyDetails ? (
                        <div className="flex flex-col">
                            <p className="text-body1">
                                <MoneyDisplay amount={amountOriginalCurr} currency={currency} />
                            </p>
                            <span className="text-caption text-fg-secondary">
                                <MoneyDisplay amount={amountBaseCurr} currency={baseCurrency} />
                            </span>
                        </div>
                    ) : null;
                },
            },
            {
                header: '',
                id: 'actions',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    // Single icon-button column — excluded from the weighted flex, frozen at its
                    // measured width here (105 px).
                    fixedPx: 105,
                },
                cell: ({ row }) => {
                    const { id: matterId, matterStateTypeCode } = row.original;
                    const isCaseClosed =
                        matterStateTypeCode === MatterStateType.COMPLETED ||
                        matterStateTypeCode === MatterStateType.OFFER_LOST ||
                        matterStateTypeCode === MatterStateType.OFFER_WON;
                    return matterId ? <MatterActionButtons matter={row.original} isDisabled={isCaseClosed} /> : null;
                },
            },
        ],
        [classifiers, showSequenceNumberColumn, sequenceNumberColumn]
    );

    return (
        <div>
            {getTitle(groupTitle, group)}
            <MattersAccordionTable
                accordionTitle={getAccordionTitle(group, 'inProgress')}
                count={getCount(group, 'inProgress')}
                passiveFilter={`matterStateTypeCode=in=(IN_PROGRESS);${passiveFilter}`}
                searchText={searchText}
                disableUrlState={disableUrlState}
                defaultExpanded={getDefaultExpanded(group, 'inProgress')}
                defaultFetched={getDefaultExpanded(group, 'inProgress')}
                rowsPerPage={rowsPerPage}
                columns={columns}
                estimatedRowHeight={ROW_HEIGHT_ESTIMATE}
                columnConfigId="matters-grouped-accordion"
                getRowHref={getMatterHref}
            />
            <MattersAccordionTable
                accordionTitle={getAccordionTitle(group, 'pending')}
                count={getCount(group, 'pending')}
                passiveFilter={`matterStateTypeCode=in=(OFFER_PENDING);${passiveFilter}`}
                searchText={searchText}
                disableUrlState={disableUrlState}
                defaultExpanded={getDefaultExpanded(group, 'pending')}
                defaultFetched={getDefaultExpanded(group, 'pending')}
                rowsPerPage={rowsPerPage}
                columns={columns}
                estimatedRowHeight={ROW_HEIGHT_ESTIMATE}
                columnConfigId="matters-grouped-accordion"
                getRowHref={getMatterHref}
            />
            <MattersAccordionTable
                accordionTitle={getAccordionTitle(group, 'completed')}
                count={getCount(group, 'completed')}
                passiveFilter={`matterStateTypeCode=in=(COMPLETED);${passiveFilter}`}
                searchText={searchText}
                disableUrlState={disableUrlState}
                defaultExpanded={getDefaultExpanded(group, 'completed')}
                defaultFetched={getDefaultExpanded(group, 'completed')}
                rowsPerPage={rowsPerPage}
                columns={columns}
                estimatedRowHeight={ROW_HEIGHT_ESTIMATE}
                columnConfigId="matters-grouped-accordion"
                getRowHref={getMatterHref}
            />
            <MattersAccordionTable
                accordionTitle={getAccordionTitle(group, 'offerWon')}
                count={getCount(group, 'offerWon')}
                passiveFilter={`matterStateTypeCode=in=(OFFER_WON);${passiveFilter}`}
                searchText={searchText}
                disableUrlState={disableUrlState}
                defaultExpanded={getDefaultExpanded(group, 'offerWon')}
                defaultFetched={getDefaultExpanded(group, 'offerWon')}
                rowsPerPage={rowsPerPage}
                columns={columns}
                estimatedRowHeight={ROW_HEIGHT_ESTIMATE}
                columnConfigId="matters-grouped-accordion"
                getRowHref={getMatterHref}
            />
            <MattersAccordionTable
                accordionTitle={getAccordionTitle(group, 'offerFailed')}
                count={getCount(group, 'offerFailed')}
                passiveFilter={`matterStateTypeCode=in=(OFFER_LOST);${passiveFilter}`}
                searchText={searchText}
                disableUrlState={disableUrlState}
                defaultExpanded={getDefaultExpanded(group, 'offerFailed')}
                defaultFetched={getDefaultExpanded(group, 'offerFailed')}
                rowsPerPage={rowsPerPage}
                columns={columns}
                estimatedRowHeight={ROW_HEIGHT_ESTIMATE}
                columnConfigId="matters-grouped-accordion"
                getRowHref={getMatterHref}
            />
        </div>
    );
};

export default MattersTableGrouped;
